import { createMaterializer } from '@wix/materializer'
import type { RefSchemas } from '@wix/thunderbolt-becky-types'
import { pointers } from '@wix/thunderbolt-becky-types'
import type { InvalidationList } from '@wix/thunderbolt-types'
import { getRefCompIdFromInflatedId } from '@wix/thunderbolt-commons'
import {
	INVALIDATIONS_NAMESPACE,
	REF_COMPONENTS_TEMPLATE_DATA_ASPECT,
	RESOLVED_REFS_NAMESPACE,
	SITE_NAMESPACE,
	COMPONENTS_WITH_SLOTS_ASPECT,
} from './constants'
import { updateModel, getModelUpdateTransformer } from './modelUpdate'
import { createBatchedRefConsumer, getRefConsumptionTransformer } from './refConsumption'
import type { ByRefsis, RawModel } from '../types'

const {
	data: {
		DATA_MAPS: { DATA, BEHAVIORS },
	},
} = pointers

/*
 DATA - resolve translated data
 BEHAVIORS - resolve fixed behaviors (with inflated sourceId/targetId)
 COMPONENTS_WITH_SLOTS_ASPECT - resolve components with slots
 */
const SITE_ASPECTS_TO_INVALIDATE: Array<string> = [DATA, BEHAVIORS, COMPONENTS_WITH_SLOTS_ASPECT]

export const getByRefsis = ({
	onInvalidation,
	rawModel = {},
	schemas,
	doNotInflateSharedParts = false,
	editorMode,
}: {
	onInvalidation: (path: Array<string>, value: unknown) => void
	rawModel?: RawModel
	schemas: RefSchemas
	doNotInflateSharedParts: boolean
	editorMode: boolean
}): ByRefsis => {
	const transformDataUpdateValue = getModelUpdateTransformer(rawModel)
	const transformRefConsumptionValue = getRefConsumptionTransformer(rawModel)
	const dynamicSchemas: RefSchemas = {}

	const getSchemaFromLocalState = (mapName: string, type: string) => {
		const staticSchema = schemas[`${mapName}.json`]?.[type]
		const dynamicSchema = dynamicSchemas[mapName]?.[type]

		return dynamicSchema || staticSchema
	}

	const materializer = createMaterializer({
		transform: (value, path) => {
			const [namespace] = path
			if (namespace === RESOLVED_REFS_NAMESPACE) {
				return transformRefConsumptionValue(value, path)
			}

			return transformDataUpdateValue(value, path)
		},
	})

	const resolveModel = (modelUpdateInvalidations: InvalidationList) =>
		materializer.batch((batchUpdate) => {
			const consumeRef = createBatchedRefConsumer(
				materializer,
				rawModel,
				batchUpdate,
				doNotInflateSharedParts,
				editorMode
			)

			for (const invalidation of modelUpdateInvalidations) {
				const [namespace, aspect, id] = invalidation
				if (namespace === SITE_NAMESPACE) {
					if (aspect === REF_COMPONENTS_TEMPLATE_DATA_ASPECT) {
						consumeRef({ refCompId: id })
						continue
					}
					if (SITE_ASPECTS_TO_INVALIDATE.includes(aspect)) {
						onInvalidation(invalidation, materializer.get(invalidation))
						continue
					}
				}
				if (namespace !== INVALIDATIONS_NAMESPACE && aspect === INVALIDATIONS_NAMESPACE) {
					const ancestorRefCompId = getRefCompIdFromInflatedId(id)
					consumeRef({ refCompId: ancestorRefCompId })
					continue
				}

				// changes in resolved ref widgets should be updated to ds carmi (valid flow only in editor)
				else if (namespace === RESOLVED_REFS_NAMESPACE) {
					onInvalidation(invalidation, materializer.get(invalidation))
				} else if (aspect === DATA) {
					// resolve translated refs (such as link) of widgets data
					onInvalidation(invalidation, materializer.get(invalidation))
				}
			}
		})

	const transaction: ByRefsis['transaction'] = (modelUpdateFn) => {
		const modelUpdateInvalidations = updateModel(
			materializer,
			rawModel,
			modelUpdateFn,
			getSchemaFromLocalState,
		)
		const modelResolutionInvalidations = resolveModel(modelUpdateInvalidations)
		for (const invalidation of modelResolutionInvalidations) {
			onInvalidation(invalidation, materializer.get(invalidation))
		}
	}

	const addDynamicSchemas = (addedSchemas: RefSchemas) => {
		for (const mapName in addedSchemas) {
			if (!dynamicSchemas[mapName]) {
				dynamicSchemas[mapName] = {}
			}
			Object.assign(dynamicSchemas[mapName], addedSchemas[mapName])
		}
	}

	return {
		transaction,
		addDynamicSchemas,
		cleanAll: () => {
			materializer.cleanAll()
		},
	}
}
